import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { authService } from '../../../services/authService';
import { companyService } from '../../../services/companyService';
import { sanitizeError } from '../../../utils/sanitizeError';
import { supabase } from '../../../lib/supabase';

/**
 * Polls supabase.auth.getUser() until the newly-created auth user row is
 * visible to the database layer. Replaces the previous fixed 2–2.5 s delay.
 * Retries up to maxAttempts times with intervalMs between each attempt.
 */
const waitForAuthUser = async (sb, userId, maxAttempts = 10, intervalMs = 300) => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const { data: { user } } = await sb?.auth?.getUser();
      if (user?.id === userId) {
        console.log(`[waitForAuthUser] Auth user confirmed after ${attempt} attempt(s)`);
        return true;
      }
    } catch (_) {
      // ignore transient errors and keep retrying
    }
    if (attempt < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  // Proceed anyway — the SECURITY DEFINER RPC handles missing sessions
  console.warn('[waitForAuthUser] Auth user not confirmed after max attempts — proceeding anyway');
  return false;
};

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    registrationNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postcode: '',
    contactPhone: '',
    contactEmail: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    agreeTerms: false,
    agreeGDPR: false
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const validatePostcode = (postcode) => {
    const ukPostcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
    return ukPostcodeRegex?.test(postcode?.trim());
  };

  const validateUKPhone = (phone) => {
    const ukPhoneRegex = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;
    return ukPhoneRegex?.test(phone?.trim());
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'companyName':
        if (!value?.trim()) error = 'Company name is required';
        else if (value?.trim()?.length < 2) error = 'Company name must be at least 2 characters';
        break;

      case 'registrationNumber':
        if (value?.trim() && !/^\d{8}$/?.test(value?.trim())) error = 'Must be 8 digits (e.g., 12345678)';
        break;

      case 'addressLine1':
        if (!value?.trim()) error = 'Address line 1 is required';
        break;

      case 'city':
        if (!value?.trim()) error = 'City is required';
        break;

      case 'postcode':
        if (!value?.trim()) error = 'Postcode is required';
        else if (!validatePostcode(value)) error = 'Invalid UK postcode format';
        break;

      case 'contactPhone':
        if (!value?.trim()) error = 'Contact phone is required';
        else if (!validateUKPhone(value)) error = 'Invalid UK phone number (e.g., 07123 456789)';
        break;

      case 'contactEmail':
        if (!value?.trim()) error = 'Contact email is required';
        else if (!validateEmail(value)) error = 'Invalid email format';
        break;

      case 'adminFirstName':
        if (!value?.trim()) error = 'First name is required';
        break;

      case 'adminLastName':
        if (!value?.trim()) error = 'Last name is required';
        break;

      case 'adminEmail':
        if (!value?.trim()) error = 'Admin email is required';
        else if (!validateEmail(value)) error = 'Invalid email format';
        break;

      case 'adminPassword':
        if (!value) error = 'Password is required';
        else if (value?.length < 8) error = 'Password must be at least 8 characters';
        else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(value))
          error = 'Password must contain uppercase, lowercase, and number';
        break;

      case 'confirmPassword':
        if (!value) error = 'Please confirm your password';
        else if (value !== formData?.adminPassword) error = 'Passwords do not match';
        break;

      case 'agreeTerms':
        if (!value) error = 'You must agree to the terms and conditions';
        break;

      case 'agreeGDPR':
        if (!value) error = 'You must agree to GDPR compliance';
        break;

      default:
        break;
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    if (touched?.[name]) {
      let error = validateField(name, fieldValue);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e?.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    let error = validateField(name, fieldValue);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData)?.forEach(key => {
      let error = validateField(key, formData?.[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    setTouched(Object.keys(formData)?.reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log('[RegistrationForm] handleSubmit triggered');

    // Guard: prevent double-submission
    if (isSubmitting || submitAttempted) return;

    if (!validateForm()) {
      console.log('[RegistrationForm] Form validation failed', errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitAttempted(true);
    setErrors({});

    try {
      // Step 0: Trial-eligibility pre-check.
      //
      // The RPC reads trial_history (SECURITY DEFINER — anon can't read the
      // table directly) and returns one of:
      //   { allowed: true,  reason: 'no_history' }
      //   { allowed: false, reason: 'already_trialed' }
      //   { allowed: false, reason: 'previously_paid_blocked' }
      //
      // The AFTER INSERT trigger on auth.users is the authoritative gate —
      // this RPC is a UX shortcut so users get a clear "you already trialed"
      // message before we send a confirmation email they can't act on.
      // See [B9UB0M58].
      const normalizedEmail = formData?.adminEmail?.trim()?.toLowerCase();
      // Wrap the RPC call: a transient network/Supabase failure here must
      // NOT block legitimate signups. The AFTER INSERT trigger on
      // auth.users is the authoritative gate, so we log and proceed.
      let eligibility = null;
      try {
        const { data, error } = await supabase?.rpc('check_trial_eligibility', { p_email: normalizedEmail });
        if (error) {
          console.warn('[RegistrationForm] check_trial_eligibility RPC error — proceeding (DB trigger is authoritative):', error?.message);
        } else {
          eligibility = data;
        }
      } catch (rpcErr) {
        console.warn('[RegistrationForm] check_trial_eligibility RPC threw — proceeding (DB trigger is authoritative):', rpcErr?.message);
      }
      if (eligibility && eligibility?.allowed === false) {
        const msg = eligibility?.reason === 'previously_paid_blocked'
          ? 'An account with this email already exists. Please log in instead.'
          : 'This email already had a free trial. Please log in to your existing account, or use a different email.';
        setErrors({ form: msg, _showLoginLink: true, _prefillEmail: normalizedEmail });
        setIsSubmitting(false);
        setSubmitAttempted(false);
        return;
      }

      // Step 1: Create auth user
      const fullName = `${formData?.adminFirstName} ${formData?.adminLastName}`;
      console.log('[RegistrationForm] Step 1: Creating auth user for', formData?.adminEmail);

      // Note: contactEmail is stored in the companies table only.
      // It does NOT trigger a separate auth/email action.
      // Only ONE confirmation email is sent — to adminEmail via supabase.auth.signUp.
      const { data: authData, error: authError } = await authService?.signUp(
        formData?.adminEmail,
        formData?.adminPassword,
        fullName,
        'admin',
        formData?.contactPhone
      );

      if (authError) {
        console.error('[RegistrationForm] Auth signUp error:', authError);
        const rawMsg = authError?.message || '';
        let friendlyMsg;
        if (
          rawMsg?.toLowerCase()?.includes('rate limit') ||
          rawMsg?.toLowerCase()?.includes('email rate limit') ||
          rawMsg?.toLowerCase()?.includes('too many') ||
          authError?.status === 429
        ) {
          friendlyMsg = 'Too many emails sent recently. Please wait a few minutes and try again.';
        } else if (rawMsg?.toLowerCase()?.includes('already registered') || rawMsg?.toLowerCase()?.includes('user already exists')) {
          friendlyMsg = 'An account with this email already exists. Please sign in instead.';
        } else {
          friendlyMsg = rawMsg || 'Failed to create account. Please try again.';
        }
        setErrors({ form: friendlyMsg });
        setSubmitAttempted(false);
        return;
      }

      // DEBUG: Log auth user ID at registration
      console.log('[DEBUG][REGISTRATION] Auth user created. user.id=', authData?.user?.id, 'email=', authData?.user?.email, 'email_confirmed_at=', authData?.user?.email_confirmed_at, 'session=', authData?.session ? 'PRESENT (auto-confirmed)' : 'NULL (email confirmation required)');

      // Step 2: Ensure session is active before creating company
      if (authData?.session?.access_token) {
        const { supabase: sb } = await import('../../../lib/supabase');
        await sb?.auth?.setSession({
          access_token: authData?.session?.access_token,
          refresh_token: authData?.session?.refresh_token
        });
        console.log('[RegistrationForm] Session set from signUp response. user.id=', authData?.user?.id);
        // Poll until auth.users row is visible to the DB (replaces fixed 2s delay)
        await waitForAuthUser(sb, authData?.user?.id);
      } else {
        // No session returned — email confirmation is required.
        // The RPC create_company_for_user uses SECURITY DEFINER and accepts
        // a fallback user ID, so it can create the company without an active session.
        console.log('[DEBUG][REGISTRATION] No session from signUp (email confirmation required). Will use fallbackUserId=', authData?.user?.id, 'for company creation via SECURITY DEFINER RPC.');
        // Poll until auth.users row is visible to the DB (replaces fixed 2.5s delay)
        const { supabase: sb } = await import('../../../lib/supabase');
        await waitForAuthUser(sb, authData?.user?.id);
      }

      // Step 2: Create company — retry up to 3 times for user_profiles trigger delay
      console.log('[RegistrationForm] Step 2: Creating company record for user.id=', authData?.user?.id);
      let companyData = null;
      let companyError = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await companyService?.createCompany({
          companyName: formData?.companyName,
          registrationNumber: formData?.registrationNumber,
          addressLine1: formData?.addressLine1,
          addressLine2: formData?.addressLine2,
          city: formData?.city,
          county: formData?.county,
          postcode: formData?.postcode,
          contactPhone: formData?.contactPhone,
          contactEmail: formData?.contactEmail
        }, authData?.user?.id);
        companyData = result?.data;
        companyError = result?.error;
        if (!companyError) break;
        console.warn(`[RegistrationForm] createCompany attempt ${attempt + 1} failed:`, companyError?.message);
        if (attempt < 2) await new Promise(r => setTimeout(r, 1500));
      }

      if (companyError) {
        console.error('[RegistrationForm] Company creation error after retries:', companyError);
        // Show duplicate registration number as a field-level error
        if (companyError?.code === 'DUPLICATE_REGISTRATION_NUMBER') {
          // Sign out the partially-created auth user so the user can retry with a different reg number
          try {
            const { supabase: sb } = await import('../../../lib/supabase');
            await sb?.auth?.signOut();
          } catch (signOutErr) {
            console.warn('[RegistrationForm] Could not sign out partial auth session:', signOutErr);
          }
          setErrors({ registrationNumber: companyError?.message });
          setTouched(prev => ({ ...prev, registrationNumber: true }));
        } else {
          setErrors({ form: 'Your account was created but we could not complete setup. Please try signing in — your account may already be ready.' });
        }
        setSubmitAttempted(false);
        return;
      }

      const companyId = companyData?.id;
      // DEBUG: Log company ID and user profile linkage at registration
      console.log('[DEBUG][REGISTRATION] Company created. company.id=', companyId, 'linked to user.id=', authData?.user?.id);

      // Verify user_profile was created by the RPC
      try {
        const { supabase: sb } = await import('../../../lib/supabase');
        const { data: profileCheck } = await sb?.from('user_profiles')?.select('id, company_id, role')?.eq('id', authData?.user?.id)?.maybeSingle();
        console.log('[DEBUG][REGISTRATION] user_profiles record:', profileCheck ? `id=${profileCheck?.id} company_id=${profileCheck?.company_id} role=${profileCheck?.role}` : 'NOT FOUND — RPC may not have created it');
      } catch (profileCheckErr) {
        console.warn('[DEBUG][REGISTRATION] Could not verify user_profiles record:', profileCheckErr);
      }

      // subscription_status stays 'inactive' until stripe-webhook processes
      // checkout.session.completed — that is the sole authority on 'trialing'.

      console.log('[RegistrationForm] Account creation complete, navigating to setup flow');
      // Navigate directly into the setup flow — the user is already authenticated.
      // A banner on the company-details page will remind them to verify their email.
      if (authData?.session) {
        navigate('/setup-flow-company-details', { state: { email: formData?.adminEmail } });
      } else {
        navigate('/email-verification-pending', { state: { email: formData?.adminEmail } });
      }
    } catch (error) {
      console.error('[RegistrationForm] Unexpected error in handleSubmit:', error);
      setErrors({ form: sanitizeError(error?.message, 'Registration failed. Please try again.') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isSubmitting;
  const emailsMatch =
    formData?.adminEmail &&
    formData?.contactEmail &&
    formData?.adminEmail?.toLowerCase()?.trim() === formData?.contactEmail?.toLowerCase()?.trim();

  return (
    <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
      {errors?.form && (
        <div className="p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm text-destructive">{errors?.form}</p>
            {errors?._showLoginLink && (
              <button
                type="button"
                onClick={() => navigate('/login', { state: { email: errors?._prefillEmail } })}
                className="text-sm font-semibold text-destructive underline underline-offset-2 hover:text-destructive/80 mt-2"
              >
                Log in instead →
              </button>
            )}
          </div>
        </div>
      )}
      <div className="space-y-4 md:space-y-6">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4 font-heading">
            Company Information
          </h3>
          <div className="space-y-4">
            <Input
              label="Company Name"
              type="text"
              name="companyName"
              placeholder="Enter your company name"
              value={formData?.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.companyName ? errors?.companyName : ''}
              required
            />

            <Input
              label="Company Registration Number"
              type="text"
              name="registrationNumber"
              placeholder="12345678 (optional)"
              description="8-digit Companies House number (optional — can be added later in settings)"
              value={formData?.registrationNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.registrationNumber ? errors?.registrationNumber : ''}
              maxLength={8}
            />
          </div>
        </div>

        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4 font-heading">
            Company Address
          </h3>
          <div className="space-y-4">
            <Input
              label="Address Line 1"
              type="text"
              name="addressLine1"
              placeholder="Building number and street name"
              value={formData?.addressLine1}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.addressLine1 ? errors?.addressLine1 : ''}
              required
            />

            <Input
              label="Address Line 2"
              type="text"
              name="addressLine2"
              placeholder="Area or district (optional)"
              value={formData?.addressLine2}
              onChange={handleChange}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="City"
                type="text"
                name="city"
                placeholder="Enter city"
                value={formData?.city}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched?.city ? errors?.city : ''}
                required
              />

              <Input
                label="County"
                type="text"
                name="county"
                placeholder="Enter county (optional)"
                value={formData?.county}
                onChange={handleChange}
              />
            </div>

            <Input
              label="Postcode"
              type="text"
              name="postcode"
              placeholder="SW1A 1AA"
              description="UK postcode format"
              value={formData?.postcode}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.postcode ? errors?.postcode : ''}
              required
            />
          </div>
        </div>

        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4 font-heading">
            Contact Details
          </h3>
          <div className="space-y-4">
            <Input
              label="Contact Phone"
              type="tel"
              name="contactPhone"
              placeholder="07123 456789"
              description="UK mobile number"
              value={formData?.contactPhone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.contactPhone ? errors?.contactPhone : ''}
              required
            />

            <Input
              label="Contact Email"
              type="email"
              name="contactEmail"
              placeholder="contact@company.co.uk"
              value={formData?.contactEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.contactEmail ? errors?.contactEmail : ''}
              required
            />
            {emailsMatch && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-blue-700">
                  Your contact email matches your admin email. Only one confirmation email will be sent.
                </p>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4 font-heading">
            Admin User Account
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                name="adminFirstName"
                placeholder="Enter first name"
                value={formData?.adminFirstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched?.adminFirstName ? errors?.adminFirstName : ''}
                required
              />

              <Input
                label="Last Name"
                type="text"
                name="adminLastName"
                placeholder="Enter last name"
                value={formData?.adminLastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched?.adminLastName ? errors?.adminLastName : ''}
                required
              />
            </div>

            <Input
              label="Admin Email"
              type="email"
              name="adminEmail"
              placeholder="admin@company.co.uk"
              description="This will be your login email"
              value={formData?.adminEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.adminEmail ? errors?.adminEmail : ''}
              required
            />

            <Input
              label="Password"
              type="password"
              name="adminPassword"
              placeholder="Create a strong password"
              description="Minimum 8 characters with uppercase, lowercase, and number"
              value={formData?.adminPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.adminPassword ? errors?.adminPassword : ''}
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              placeholder="Re-enter your password"
              value={formData?.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.confirmPassword ? errors?.confirmPassword : ''}
              required
            />
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <div className="space-y-1">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={formData?.agreeTerms}
                onChange={handleChange}
                onBlur={handleBlur}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent flex-shrink-0"
              />
              <span className="text-sm text-foreground">
                I agree to the{' '}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline hover:opacity-80"
                  onClick={(e) => e?.stopPropagation()}
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline hover:opacity-80"
                  onClick={(e) => e?.stopPropagation()}
                >
                  Privacy Policy
                </a>
                <span className="text-destructive ml-0.5">*</span>
              </span>
            </label>
            {touched?.agreeTerms && errors?.agreeTerms && (
              <p className="text-xs text-destructive pl-7">{errors?.agreeTerms}</p>
            )}
          </div>

          <Checkbox
            name="agreeGDPR"
            label="I agree to GDPR compliance and data processing"
            checked={formData?.agreeGDPR}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched?.agreeGDPR ? errors?.agreeGDPR : ''}
            required
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-4">
        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isSubmitting}
          disabled={isSubmitting}
          iconName="CheckCircle2"
          iconPosition="left"
          className="w-full sm:flex-1"
        >
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </Button>

        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => navigate('/login')}
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          Already have an account?
        </Button>
      </div>
      {isSubmitting && (
        <p className="text-xs text-center text-muted-foreground">
          Setting up your account&hellip;
        </p>
      )}
    </form>
  );
};

export default RegistrationForm;